// @givmo/fundraisers-module — the email designer's hand editor, drawn over its live preview (EMC-WEB10; D-EMC-20
// revised by Jonathan, 2026-10-31: "it's ok to have a basic editor where we have like title, body text, etc." …
// "each block of text gets text box editor … so the user can manually tweak a bit what the AI did").
//
// ══ WHAT A PERSON CAN DO BY HAND ═══════════════════════════════════════════════════════════════════
//
// * **Words, in place.** A click on a heading, a paragraph, a list item or a button makes its words editable
//   right where they are, in the email's own type, with a bar in the tools row above the preview: bold,
//   italic, link and the first-name merge field (the "First name" chip, with what it says when a name isn't on
//   file). Enter saves a heading, a list item or a button; a paragraph takes Enter as a new line; Done saves,
//   Esc puts back.
// * **Each block, on hover or focus:** move up, move down, swap its photo (from Collateral) and delete.
// * **An HTML block is the AI's alone:** it says "Ask the assistant to change this part", and offers nothing
//   to do by hand.
// * **The layout and the design stay the AI's:** colours, type, sizes, columns and new blocks are asked of it.
//
// ══ HOW IT SAVES ═══════════════════════════════════════════════════════════════════════════════════
//
// Every hand edit is the whole document written back through the screen's own change route (`onSave`), the
// one the AI's change operation runs, so a link or an image an email can't carry, a size past its cap, an
// empty block, is refused exactly as the AI's would be: the server's words are said and the person's words
// stay in the editor to fix. The page puts each saved edit on the one undo stack the AI's changes share.
//
// Typed words are never dropped: a press or a move of focus anywhere outside the words and their bar commits
// them (`commitOpen`), and every act of the page's own waits for that commit first (`commitRef`).
//
// ══ HOW IT DRAWS ═══════════════════════════════════════════════════════════════════════════════════
//
// The preview is the server's render in a frame that runs no script (`message-email-frame.tsx`); this module's
// script reaches into it (same origin) by the marks the server put on each block's cell for the preview alone
// (`data-emc-block="3"` or `"3.1.0"` and `data-emc-kind`). Its outlines are a constructed stylesheet adopted
// beside the email's own (the consoles' `style-src 'self'` refuses a style element), and its bars sit above the
// frame in the page, scaled with it. The words' bar is the exception: it sits in the page's tools row above the
// preview (`toolsSlot`), so it never covers any of the email (the KCC hand edit, 2026-11-01).

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { yieldAssistantSheet } from '@givmo/assistant-panel';
import { CollateralPickerDialog } from '@givmo/org-console';
import { Button, Icon, cn, testIdProp, toast } from '@givmo/design-system';
import {
  ArrowDown,
  ArrowUp,
  Bold,
  Check,
  ImageIcon,
  Italic,
  Link2,
  Lock,
  Pencil,
  Trash2,
} from 'lucide-react';
import { EMAIL_DESIGNER_COPY as S } from './email-designer-copy';
import {
  HAND_LINK_ATTR,
  blockAt,
  canMove,
  deleteBlock,
  drawInline,
  handEditable,
  historyKeyOf,
  inlineContent,
  inlineWords,
  linkAddress,
  linkInputText,
  mergeChip,
  moveBlock,
  parseBlockPath,
  parseLinkInput,
  readInline,
  replaceBlock,
  sameInline,
  type BlockPath,
  type InlineNode,
  type LinkTarget,
} from './email-hand-edit';

type Json = Record<string, unknown>;

const BLOCK_MARK = 'data-emc-block';
const KIND_MARK = 'data-emc-kind';
const HOVER_MARK = 'data-hand-hover';
const EDITING_MARK = 'data-hand-editing';
/** The longest button words the document takes (`MAX_BUTTON_LABEL`). */
const MAX_BUTTON_LABEL = 80;
/** How long the block rail outlives the pointer leaving its block, for the way across to it. */
const HOVER_GRACE_MS = 300;
/** What a panel may need under or over the words (its tallest, with an error showing). */
const PANEL_ROOM = 270;
/** A temporary address `createLink` writes, swapped for the link's target at once. */
const LINK_SENTINEL = 'https://givmo-hand-link.invalid/';

const FRAME_SHEET = `
[${BLOCK_MARK}]{transition:outline-color .12s ease;outline:2px solid transparent;outline-offset:-2px;}
[${HOVER_MARK}]{outline-color:rgba(59,91,219,.32);cursor:text;}
[${HOVER_MARK}][${KIND_MARK}="image"],[${HOVER_MARK}][${KIND_MARK}="divider"],[${HOVER_MARK}][${KIND_MARK}="spacer"],[${HOVER_MARK}][${KIND_MARK}="columns"],[${HOVER_MARK}][${KIND_MARK}="employer_match"]{cursor:default;}
[${HOVER_MARK}][${KIND_MARK}="html"]{outline-style:dashed;outline-color:rgba(107,114,128,.45);cursor:default;}
[${BLOCK_MARK}]:focus-visible{outline-color:#3b5bdb;}
[${EDITING_MARK}]{outline:2px solid #3b5bdb !important;outline-offset:3px;border-radius:2px;cursor:text;}
[contenteditable="true"]:focus{outline:none;}
`;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The frame an image block keeps when its photo is swapped. */
interface ImageFrame {
  width: number;
  height: number;
}

interface Target {
  path: BlockPath;
  kind: string | null;
  cell: HTMLElement;
}

interface Editing extends Target {
  /** The element whose words are edited: the heading, the paragraph, the list item or the button's link. */
  element: HTMLElement;
  /** A list item's place in its list. */
  item: number | null;
  /** What it showed before, put back on Esc. */
  original: Node[];
  /** The block as it was when the words opened: a redraw commits them only over this same block. */
  block: Json;
  content: InlineNode[];
  label: string;
}

function rectOf(element: Element, scale: number): Rect {
  const r = element.getBoundingClientRect();
  return {
    top: r.top * scale,
    left: r.left * scale,
    width: r.width * scale,
    height: r.height * scale,
  };
}

/**
 * The element an event or a range lands in. The frame's nodes are its own window's, so `instanceof` the
 * page's `Element` is always false for them: a node is read by its type.
 */
function elementOf(node: unknown): Element | null {
  if (node === null || typeof node !== 'object' || !('nodeType' in node)) return null;
  const typed = node as Node;
  if (typed.nodeType === 1) return typed as Element;
  return typed.parentElement;
}

function targetOf(node: EventTarget | null): Target | null {
  const element = elementOf(node);
  const cell = element?.closest<HTMLElement>(`[${BLOCK_MARK}]`) ?? null;
  if (cell === null) return null;
  const path = parseBlockPath(cell.getAttribute(BLOCK_MARK));
  if (path === null) return null;
  return { path, kind: cell.getAttribute(KIND_MARK), cell };
}

/** The element of a block whose words a click edits, or null. */
function wordsElement(target: Target, clicked: EventTarget | null): HTMLElement | null {
  const { cell, kind } = target;
  if (kind === 'heading') return cell.querySelector<HTMLElement>('h1, h2, h3, h4');
  if (kind === 'paragraph') return cell.querySelector<HTMLElement>('p');
  if (kind === 'button') return cell.querySelector<HTMLElement>('a');
  if (kind === 'list') {
    const at = elementOf(clicked)?.closest<HTMLElement>('li') ?? null;
    return at ?? cell.querySelector<HTMLElement>('li');
  }
  return null;
}

/** A block's name, by its kind, in its controls' words ("Delete this paragraph"). */
function kindName(kind: string | null): string {
  return S.hand.kinds[kind ?? ''] ?? 'block';
}

function samePath(a: BlockPath | null | undefined, b: BlockPath | null | undefined): boolean {
  return a != null && b != null && a.join('.') === b.join('.');
}

export interface HandEditLayerProps {
  /** The preview frame's drawn document, and a count that moves on every draw. */
  doc: Document | null;
  drawn: number;
  /** The preview's scale: the email is drawn at its own width and shrunk to the space it has. */
  scale: number;
  /** What holds the frame and these bars: leaving it lets the hovered block go. */
  container: HTMLElement | null;
  /** The email's document as stored. */
  body: Json;
  /** Off while the assistant works, or once the email can't change. */
  enabled: boolean;
  orgRef: string;
  /** The organization's own date, `YYYY-MM-DD`: the image picker offers nothing dated before it. */
  today: string;
  /** Whether this person may add a file to Collateral from the image picker. */
  canUploadCollateral: boolean;
  /** Write the whole document; true when the server took it. */
  onSave: (body: Json) => Promise<boolean>;
  /** Filled with "save what's open" (null at once when nothing is), which the page runs before a turn
   *  of the assistant. */
  commitRef?: MutableRefObject<(() => Promise<boolean> | null) | null>;
  /** The page's Undo or Redo, asked from the frame's keyboard (Cmd/Ctrl+Z, with Shift for Redo): the frame
   *  keeps its own keys, so the page never hears them. Never while words are open: theirs is the browser's. */
  onHistoryKey?: (which: 'undo' | 'redo') => void;
  /** The page's tools row above the preview, where the words' bar sits while words are open: outside the
   *  email, so it covers none of it. Without one, the bar sits under the words. */
  toolsSlot?: HTMLElement | null;
  /** Told when words open and close: the page keeps its own keys off them meanwhile. */
  onWordsOpen?: (open: boolean) => void;
}

export function HandEditLayer({
  doc,
  drawn,
  scale,
  container,
  body,
  enabled,
  orgRef,
  today,
  canUploadCollateral,
  onSave,
  commitRef,
  onHistoryKey,
  toolsSlot = null,
  onWordsOpen,
}: HandEditLayerProps) {
  const [hovered, setHovered] = useState<Target | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState<'link' | 'merge' | null>(null);
  const [picker, setPicker] = useState<{ path: BlockPath; frame: ImageFrame | null } | null>(null);
  const [tick, setTick] = useState(0);
  const bars = useRef<HTMLDivElement>(null);
  const hoverGrace = useRef<ReturnType<typeof setTimeout> | null>(null);
  const committing = useRef<Promise<boolean> | null>(null);
  const holdHover = useCallback(() => {
    if (hoverGrace.current !== null) clearTimeout(hoverGrace.current);
    hoverGrace.current = null;
  }, []);
  const letHoverGo = useCallback(() => {
    holdHover();
    hoverGrace.current = setTimeout(() => setHovered(null), HOVER_GRACE_MS);
  }, [holdHover]);
  useEffect(() => holdHover, [holdHover]);
  const range = useRef<Range | null>(null);
  const editingRef = useRef<Editing | null>(null);
  editingRef.current = editing;
  const bodyRef = useRef(body);
  bodyRef.current = body;
  const historyKey = useRef(onHistoryKey);
  historyKey.current = onHistoryKey;
  // The bars are this layer's own and the tools row's: a press or focus there stays with the open words.
  const slotRef = useRef(toolsSlot);
  slotRef.current = toolsSlot;
  const inBars = useCallback(
    (node: Node | null) =>
      node !== null &&
      (bars.current?.contains(node) === true || slotRef.current?.contains(node) === true),
    [],
  );
  const wordsOpen = editing !== null;
  const wordsOpenChanged = useRef(onWordsOpen);
  wordsOpenChanged.current = onWordsOpen;
  useEffect(() => {
    wordsOpenChanged.current?.(wordsOpen);
  }, [wordsOpen]);
  useEffect(() => () => wordsOpenChanged.current?.(false), []);

  // ── The frame's outlines, and each block reachable by keyboard ────────────────────────────────────
  useEffect(() => {
    if (doc === null) return;
    const win = doc.defaultView as (Window & typeof globalThis) | null;
    if (win !== null && typeof win.CSSStyleSheet === 'function' && 'adoptedStyleSheets' in doc) {
      const sheet = new win.CSSStyleSheet();
      if (typeof sheet.replaceSync === 'function') {
        sheet.replaceSync(FRAME_SHEET);
        doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
      }
    }
    doc.querySelectorAll<HTMLElement>(`[${BLOCK_MARK}]`).forEach((cell) => {
      if (!enabled) {
        cell.removeAttribute('tabindex');
        return;
      }
      cell.tabIndex = 0;
      const kind = cell.getAttribute(KIND_MARK);
      cell.setAttribute(
        'aria-label',
        kind === 'html' ? S.hand.lockedBlock : S.hand.blockLabel(kindName(kind)),
      );
    });
    // A new draw is a new document. The page commits what's open before each of its own acts, so words
    // still open here met a change from elsewhere: they are saved if their block is untouched, and the
    // person is told if it isn't, never dropped without a word.
    const open = committing.current === null ? editingRef.current : null;
    if (committing.current !== null) {
      // This draw is the save's own: `writeOpen` closes the words when it's done.
    } else if (open !== null && !open.element.isConnected) {
      if (JSON.stringify(blockAt(bodyRef.current, open.path)) === JSON.stringify(open.block)) {
        void commitRef?.current?.();
      } else {
        toast.error(S.hand.overwritten);
        setEditing(null);
        setPanel(null);
      }
    } else {
      setEditing(null);
      setPanel(null);
    }
    setHovered(null);
    // `commitRef` is a ref, and `editingRef` and `bodyRef` are read as of this draw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, drawn, enabled]);

  // The hovered block's outline is a mark on its cell.
  useEffect(() => {
    if (doc === null) return;
    doc.querySelectorAll(`[${HOVER_MARK}]`).forEach((cell) => cell.removeAttribute(HOVER_MARK));
    if (hovered !== null && enabled && editing === null) hovered.cell.setAttribute(HOVER_MARK, '');
  }, [doc, hovered, editing, enabled, drawn]);

  // Leaving the preview (the frame and these bars alike) lets the hovered block go: the pointer leaving
  // it, and keyboard focus leaving it too. A block is marked as much by focus (Tab onto it, or onto its
  // rail) as by the pointer, and focus that went on to the page (the assistant's close, a header button)
  // left the block outlined and its rail standing (the unified-panel user test, 2026-11-01).
  useEffect(() => {
    if (container === null) return;
    let check: ReturnType<typeof setTimeout> | null = null;
    const focusLeft = () => {
      if (check !== null) clearTimeout(check);
      check = setTimeout(() => {
        check = null;
        // Focus inside the frame reads as the frame element here; the pointer still over it holds it.
        const active = document.activeElement;
        if (active !== null && container.contains(active)) return;
        if (container.matches(':hover')) return;
        letHoverGo();
      }, 0);
    };
    container.addEventListener('mouseleave', letHoverGo);
    container.addEventListener('focusout', focusLeft);
    return () => {
      if (check !== null) clearTimeout(check);
      container.removeEventListener('mouseleave', letHoverGo);
      container.removeEventListener('focusout', focusLeft);
    };
  }, [container, letHoverGo]);

  // The stage's scroll moves where the rail may sit.
  useEffect(() => {
    const stage = container?.parentElement;
    if (stage == null) return;
    const scrolled = () => setTick((n) => n + 1);
    stage.addEventListener('scroll', scrolled, { passive: true });
    return () => stage.removeEventListener('scroll', scrolled);
  }, [container]);

  // ── Start, save, put back ────────────────────────────────────────────────────────────────────────
  const start = useCallback(
    (target: Target, clicked: EventTarget | null) => {
      if (doc === null) return;
      const element = wordsElement(target, clicked);
      const block = blockAt(bodyRef.current, target.path);
      if (element === null || block === null) return;
      let item: number | null = null;
      let content: InlineNode[] = [];
      let label = '';
      if (target.kind === 'button') {
        label = typeof block.label === 'string' ? block.label : (element.textContent ?? '');
      } else if (target.kind === 'list') {
        const items = Array.isArray(block.items) ? block.items : [];
        item = Array.from(element.parentElement?.children ?? []).indexOf(element);
        const found = items[item] as Json | undefined;
        if (found === undefined) return;
        content = inlineContent(found.content);
      } else {
        content = inlineContent(block.content);
      }
      const original = Array.from(element.childNodes, (node) => node.cloneNode(true));
      if (target.kind !== 'button') {
        const linkColor = element.querySelector<HTMLElement>('a')?.style.color || undefined;
        drawInline(doc, element, content, { chipLabel: S.firstNameChip, linkColor });
      }
      element.setAttribute('contenteditable', 'true');
      element.spellcheck = true;
      element.setAttribute(EDITING_MARK, '');
      element.setAttribute('role', 'textbox');
      element.setAttribute('aria-multiline', target.kind === 'paragraph' ? 'true' : 'false');
      element.setAttribute('aria-label', S.hand.editing(kindName(target.kind)));
      element.focus();
      // The caret at the end of the words.
      const selection = doc.getSelection();
      if (selection !== null) {
        const end = doc.createRange();
        end.selectNodeContents(element);
        end.collapse(false);
        selection.removeAllRanges();
        selection.addRange(end);
      }
      setHovered(null);
      setEditing({ ...target, element, item, original, block, content, label });
    },
    [doc],
  );

  const stop = useCallback((open: Editing, putBack: boolean) => {
    open.element.removeAttribute('contenteditable');
    open.element.removeAttribute(EDITING_MARK);
    open.element.removeAttribute('role');
    open.element.removeAttribute('aria-multiline');
    open.element.removeAttribute('aria-label');
    if (putBack) open.element.replaceChildren(...open.original.map((node) => node.cloneNode(true)));
    setEditing(null);
    setPanel(null);
  }, []);

  const save = useCallback(
    async (next: Json): Promise<boolean> => {
      setSaving(true);
      try {
        return await onSave(next);
      } finally {
        setSaving(false);
      }
    },
    [onSave],
  );

  /** Save what's open; true when it was saved (or needed no save), false while it stays open to fix. */
  const writeOpen = useCallback(async (): Promise<boolean> => {
    const open = editingRef.current;
    if (open === null) return true;
    const block = blockAt(bodyRef.current, open.path);
    if (block === null) {
      stop(open, true);
      return true;
    }
    let next: Json;
    if (open.kind === 'button') {
      const label = (open.element.textContent ?? '').replace(/\s+/g, ' ').trim();
      if (label.length === 0) {
        toast.error(S.hand.emptyButton);
        return false;
      }
      if (label.length > MAX_BUTTON_LABEL) {
        toast.error(S.hand.longButton(MAX_BUTTON_LABEL));
        return false;
      }
      if (label === open.label) {
        stop(open, false);
        return true;
      }
      next = { ...block, label };
    } else {
      const content = readInline(open.element);
      if (inlineWords(content).trim().length === 0) {
        toast.error(S.hand.emptyWords);
        return false;
      }
      if (sameInline(content, open.content)) {
        stop(open, true);
        return true;
      }
      if (open.kind === 'list' && open.item !== null) {
        const items = [...(block.items as Json[])];
        items[open.item] = { ...items[open.item], content };
        next = { ...block, items };
      } else {
        next = { ...block, content };
      }
    }
    // Saving: the words stay as typed, no longer editable, until the new render replaces them.
    open.element.setAttribute('contenteditable', 'false');
    setPanel(null);
    const saved = await save(replaceBlock(bodyRef.current, open.path, next));
    if (!saved) {
      // Refused: the person's words stay open to fix.
      open.element.setAttribute('contenteditable', 'true');
      open.element.focus();
      return false;
    }
    stop(open, false);
    return true;
  }, [save, stop]);

  /** `writeOpen`, once: a press outside and the act it presses wait on the same save. */
  const commitOpen = useCallback((): Promise<boolean> => {
    committing.current ??= writeOpen().finally(() => {
      committing.current = null;
    });
    return committing.current;
  }, [writeOpen]);

  /** Save what's open: its promise, or null at once when nothing is open. */
  const commit = useCallback(
    (): Promise<boolean> | null => (editingRef.current === null ? null : commitOpen()),
    [commitOpen],
  );

  useEffect(() => {
    if (commitRef === undefined) return;
    commitRef.current = commit;
    return () => {
      commitRef.current = null;
    };
  }, [commit, commitRef]);

  // The email locking closes what's open by putting it back: the server would refuse it. The assistant's
  // own turn never gets here with words open: the page saves them first.
  useEffect(() => {
    if (!enabled && editingRef.current !== null && committing.current === null) {
      stop(editingRef.current, true);
    }
  }, [enabled, stop]);

  // A press anywhere outside the words and their bar commits them, in the page around the frame as in it.
  useEffect(() => {
    const outside = (event: Event) => {
      const at = event.target as Node | null;
      if (editingRef.current === null || inBars(at)) return;
      // A dialog of the bar's own would sit in a portal; the bar has none, so a press there is outside.
      void commitOpen();
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [commitOpen]);

  // ── The frame's own events ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (doc === null || !enabled) return;
    const over = (event: Event) => {
      if (editingRef.current !== null) return;
      const target = targetOf(event.target);
      // The rail stands off the email's edge: the way across to it is no block at all.
      if (target === null) {
        letHoverGo();
        return;
      }
      holdHover();
      setHovered((current) => (samePath(current?.path, target.path) ? current : target));
    };
    const focus = (event: Event) => {
      if (editingRef.current !== null) return;
      const target = targetOf(event.target);
      if (target !== null && event.target === target.cell) setHovered(target);
    };
    const click = (event: MouseEvent) => {
      const open = editingRef.current;
      if (open !== null) {
        if (open.element.contains(event.target as Node)) return;
        event.preventDefault();
        void commitOpen();
        return;
      }
      const target = targetOf(event.target);
      if (target === null || !handEditable(target.kind).words) return;
      event.preventDefault();
      start(target, event.target);
    };
    const keydown = (event: KeyboardEvent) => {
      const open = editingRef.current;
      if (open === null) {
        const which = historyKeyOf(event);
        if (which !== null) {
          event.preventDefault();
          historyKey.current?.(which);
          return;
        }
        // A focused block opens its words on Enter.
        const target = targetOf(event.target);
        if (event.key === 'Enter' && target !== null && event.target === target.cell) {
          if (handEditable(target.kind).words) {
            event.preventDefault();
            start(target, null);
          }
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        stop(open, true);
        open.cell.focus();
        return;
      }
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k' &&
        open.kind !== 'button'
      ) {
        event.preventDefault();
        openPanel('link');
        return;
      }
      if ((event.metaKey || event.ctrlKey) && open.kind === 'button' && /^[bi]$/i.test(event.key)) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        // A paragraph's Enter is a new line (Done or a click outside saves it); the rest are one line.
        if (open.kind === 'paragraph') lineBreak(doc);
        else void commitOpen();
      }
    };
    const paste = (event: ClipboardEvent) => {
      if (editingRef.current === null) return;
      event.preventDefault();
      const text = event.clipboardData?.getData('text/plain') ?? '';
      const words = editingRef.current.kind === 'paragraph' ? text : text.replace(/\s*\n\s*/g, ' ');
      insertText(doc, words);
    };
    const input = () => setTick((n) => n + 1);
    // The caret moving into or out of a link changes what the bar says.
    const selection = () => {
      if (editingRef.current !== null) setTick((n) => n + 1);
    };
    // Focus leaving the frame (Tab, or a click on the page) commits the words, unless it went to their own
    // panel, or the window itself lost focus and the frame still holds it.
    const focusout = () => {
      setTimeout(() => {
        const open = editingRef.current;
        const active = document.activeElement;
        if (open === null || active === doc.defaultView?.frameElement) return;
        if (inBars(active)) return;
        void commitOpen();
      }, 0);
    };
    doc.addEventListener('focusout', focusout);
    doc.addEventListener('mouseover', over);
    doc.addEventListener('focusin', focus);
    doc.addEventListener('click', click);
    doc.addEventListener('keydown', keydown);
    doc.addEventListener('paste', paste);
    doc.addEventListener('input', input);
    doc.addEventListener('selectionchange', selection);
    // An email taller than its frame until a late image lands scrolls inside it: the bars follow.
    doc.addEventListener('scroll', input, true);
    return () => {
      doc.removeEventListener('focusout', focusout);
      doc.removeEventListener('mouseover', over);
      doc.removeEventListener('focusin', focus);
      doc.removeEventListener('click', click);
      doc.removeEventListener('keydown', keydown);
      doc.removeEventListener('paste', paste);
      doc.removeEventListener('input', input);
      doc.removeEventListener('selectionchange', selection);
      doc.removeEventListener('scroll', input, true);
    };
    // `openPanel` reads only refs and state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, drawn, enabled, start, stop, commitOpen, holdHover, letHoverGo]);

  // ── The bar's acts ───────────────────────────────────────────────────────────────────────────────
  const keepRange = () => {
    const selection = doc?.getSelection();
    range.current =
      selection !== null && selection !== undefined && selection.rangeCount > 0
        ? selection.getRangeAt(0).cloneRange()
        : null;
  };
  const restoreRange = () => {
    const open = editingRef.current;
    if (doc === null || open === null) return null;
    open.element.focus();
    const selection = doc.getSelection();
    const saved = range.current;
    if (
      selection !== null &&
      saved !== null &&
      open.element.contains(saved.commonAncestorContainer)
    ) {
      selection.removeAllRanges();
      selection.addRange(saved);
      return saved;
    }
    return selection !== null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  };
  function openPanel(which: 'link' | 'merge') {
    keepRange();
    setPanel(which);
  }

  const mark = (command: 'bold' | 'italic') => {
    if (doc === null) return;
    restoreRange();
    if (typeof doc.execCommand === 'function') doc.execCommand(command);
    setTick((n) => n + 1);
  };

  const currentLink = (): HTMLElement | null => {
    const saved = range.current;
    return (
      elementOf(saved?.startContainer ?? null)?.closest<HTMLElement>(`a[${HAND_LINK_ATTR}]`) ?? null
    );
  };

  const applyLink = (target: LinkTarget, words: string) => {
    const open = editingRef.current;
    if (doc === null || open === null) return;
    const existing = currentLink();
    const at = restoreRange();
    if (existing !== null && open.element.contains(existing)) {
      existing.setAttribute(HAND_LINK_ATTR, JSON.stringify(target));
    } else if (at !== null && !at.collapsed) {
      let made = false;
      if (typeof doc.execCommand === 'function') {
        doc.execCommand('createLink', false, LINK_SENTINEL);
        doc.querySelectorAll<HTMLElement>(`a[href="${LINK_SENTINEL}"]`).forEach((link) => {
          link.removeAttribute('href');
          link.setAttribute(HAND_LINK_ATTR, JSON.stringify(target));
          link.style.cssText = 'color:inherit;text-decoration:underline;';
          made = true;
        });
      }
      if (!made) {
        const link = doc.createElement('a');
        link.setAttribute(HAND_LINK_ATTR, JSON.stringify(target));
        link.style.cssText = 'color:inherit;text-decoration:underline;';
        link.append(at.extractContents());
        at.insertNode(link);
      }
    } else if (at !== null) {
      const link = doc.createElement('a');
      link.setAttribute(HAND_LINK_ATTR, JSON.stringify(target));
      link.style.cssText = 'color:inherit;text-decoration:underline;';
      link.textContent = words;
      at.insertNode(link);
      caretAfter(doc, link);
    }
    setPanel(null);
    setTick((n) => n + 1);
  };

  const removeLink = () => {
    const existing = currentLink();
    if (existing !== null) existing.replaceWith(...Array.from(existing.childNodes));
    restoreRange();
    setPanel(null);
    setTick((n) => n + 1);
  };

  const insertFirstName = (fallback: string) => {
    if (doc === null) return;
    const at = restoreRange();
    if (at === null) return;
    const chip = mergeChip(doc, fallback, S.firstNameChip);
    at.deleteContents();
    at.insertNode(chip);
    caretAfter(doc, chip);
    setPanel(null);
    setTick((n) => n + 1);
  };

  // ── A block's own acts ──────────────────────────────────────────────────────────────────────────
  const move = async (path: BlockPath, direction: -1 | 1) => {
    await save(moveBlock(bodyRef.current, path, direction));
  };
  const remove = async (path: BlockPath) => {
    await save(deleteBlock(bodyRef.current, path));
  };

  // ── Where the bars sit ──────────────────────────────────────────────────────────────────────────
  const shown = editing ?? hovered;
  const box = useMemo(
    () => (shown === null ? null : rectOf(editing?.element ?? shown.cell, scale)),
    // `tick` follows typing (the words' box grows); `drawn` a new render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shown, editing, scale, tick, drawn],
  );
  const cellBox = useMemo(
    () => (shown === null ? null : rectOf(shown.cell, scale)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shown, scale, tick, drawn],
  );

  if (doc === null) return null;
  // The link the caret is in, while words are open: the bar says where it goes.
  const caretLink = (() => {
    if (editing === null) return null;
    const link =
      elementOf(doc.getSelection()?.anchorNode ?? null)?.closest<HTMLElement>(
        `a[${HAND_LINK_ATTR}]`,
      ) ?? null;
    if (link === null || !editing.element.contains(link)) return null;
    try {
      return JSON.parse(link.getAttribute(HAND_LINK_ATTR) ?? 'null') as LinkTarget | null;
    } catch {
      return null;
    }
  })();
  const pickerBlock = picker === null ? null : blockAt(body, picker.path);
  const stage = container?.parentElement ?? null;
  const visible =
    stage === null || container === null
      ? null
      : {
          top: stage.scrollTop - container.offsetTop,
          bottom: stage.scrollTop - container.offsetTop + stage.clientHeight,
        };

  return (
    <div ref={bars}>
      {enabled && editing === null && hovered !== null && cellBox !== null && (
        <BlockControls
          target={hovered}
          onHold={holdHover}
          onLetGo={letHoverGo}
          box={cellBox}
          visibleTop={(container?.parentElement?.scrollTop ?? 0) - (container?.offsetTop ?? 0)}
          // The stage's grey beside the email is the rail's room: the sheet sits centred in it.
          room={
            container === null
              ? Number.POSITIVE_INFINITY
              : (container.parentElement?.clientWidth ?? Number.POSITIVE_INFINITY) -
                container.offsetLeft
          }
          body={body}
          busy={saving}
          onMove={(direction) => void move(hovered.path, direction)}
          onDelete={() => void remove(hovered.path)}
          onSwap={() => setPicker({ path: hovered.path, frame: frameOf(hovered.cell) })}
        />
      )}
      {editing !== null && box !== null && (
        <EditBar
          kind={editing.kind}
          box={box}
          visible={visible}
          saving={saving}
          panel={panel}
          slot={toolsSlot}
          linkHere={
            caretLink === null
              ? null
              : linkAddress(caretLink, {
                  appeal: S.hand.linkAppealPage,
                  join: S.hand.linkJoinPage,
                })
          }
          linkWords={() => {
            const text = range.current?.toString() ?? '';
            return text;
          }}
          existingLink={() => {
            const link = currentLink();
            if (link === null) return null;
            try {
              return JSON.parse(link.getAttribute(HAND_LINK_ATTR) ?? 'null') as LinkTarget | null;
            } catch {
              return null;
            }
          }}
          onBold={() => mark('bold')}
          onItalic={() => mark('italic')}
          onOpenPanel={openPanel}
          onClosePanel={() => {
            setPanel(null);
            restoreRange();
          }}
          onLink={applyLink}
          onRemoveLink={removeLink}
          onFirstName={insertFirstName}
          onCancel={() => {
            stop(editing, true);
          }}
          onDone={() => void commitOpen()}
        />
      )}
      <CollateralPickerDialog
        open={picker !== null}
        onOpenChange={(open) => !open && setPicker(null)}
        orgRef={orgRef}
        today={today}
        kinds={['image']}
        title={S.hand.pickerTitle}
        body={S.hand.pickerBody}
        confirmLabel={S.hand.pickerUse}
        canUpload={canUploadCollateral}
        applying={saving}
        onChoose={([item]) => {
          if (item === undefined || picker === null || pickerBlock === null) return;
          const { path, frame } = picker;
          void save(
            replaceBlock(bodyRef.current, path, {
              ...pickerBlock,
              // The new picture takes the block's frame: it never grows the block.
              ...(typeof pickerBlock.height === 'number' || frame === null ? {} : frame),
              src: item.url,
              alt: (item.description ?? item.title).slice(0, 300),
            }),
          ).then((saved) => {
            if (saved) setPicker(null);
          });
        }}
        testId="email-hand-picker"
      />
    </div>
  );
}

/** The frame an image block shows at, in the email's own pixels (the renderer's `width`), or null before it
 *  has loaded. */
function frameOf(cell: HTMLElement): ImageFrame | null {
  const image = cell.querySelector('img');
  const width = Number(image?.getAttribute('width'));
  const drawn = image?.getBoundingClientRect();
  if (!width || drawn == null || drawn.width <= 0 || drawn.height <= 0) return null;
  return { width, height: Math.max(1, Math.round((width * drawn.height) / drawn.width)) };
}

function lineBreak(doc: Document) {
  if (typeof doc.execCommand === 'function' && doc.execCommand('insertLineBreak')) return;
  const selection = doc.getSelection();
  if (selection === null || selection.rangeCount === 0) return;
  const at = selection.getRangeAt(0);
  at.deleteContents();
  const br = doc.createElement('br');
  at.insertNode(br);
  caretAfter(doc, br);
}

function insertText(doc: Document, text: string) {
  if (typeof doc.execCommand === 'function' && doc.execCommand('insertText', false, text)) return;
  const selection = doc.getSelection();
  if (selection === null || selection.rangeCount === 0) return;
  const at = selection.getRangeAt(0);
  at.deleteContents();
  const node = doc.createTextNode(text);
  at.insertNode(node);
  caretAfter(doc, node);
}

function caretAfter(doc: Document, node: Node) {
  const selection = doc.getSelection();
  if (selection === null) return;
  const after = doc.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  selection.removeAllRanges();
  selection.addRange(after);
}

// ── The block's controls ───────────────────────────────────────────────────────────────────────────

const RAIL_GAP = 8;
const RAIL_WIDTH = 34;

function BlockControls({
  target,
  box,
  room,
  visibleTop,
  body,
  busy,
  onHold,
  onLetGo,
  onMove,
  onDelete,
  onSwap,
}: {
  target: Target;
  /** The pointer reached the controls, and left them. */
  onHold: () => void;
  onLetGo: () => void;
  box: Rect;
  /** The preview's width, scaled: how far right the rail may sit. */
  room: number;
  /** Where the preview's scroll has reached, in the bars' own pixels. */
  visibleTop: number;
  body: Json;
  busy: boolean;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  onSwap: () => void;
}) {
  const can = handEditable(target.kind);
  const name = kindName(target.kind);
  if (target.kind === 'html') {
    return (
      <div
        className="pointer-events-none absolute z-20 flex -translate-x-full items-center gap-6 whitespace-nowrap rounded-md border border-border-subtle bg-surface px-10 py-5 font-sans text-s12 tracking-givmo text-text-secondary shadow-sm"
        // Above the block's top edge, right-aligned: clear of its words.
        style={{ top: Math.max(0, box.top - 24), left: box.left + box.width - 6 }}
        {...testIdProp('email-hand-locked')}
      >
        <Icon icon={Lock} size={12} aria-hidden />
        {S.hand.askAssistant}
      </div>
    );
  }
  // A rail just outside the email's edge, in the preview's margin, so it never covers the words
  // (Klaviyo's block rail); inside the block's corner where the margin is too narrow (the phone width).
  const outside = box.left + box.width + RAIL_GAP + RAIL_WIDTH <= room;
  return (
    <div
      role="toolbar"
      aria-label={S.hand.blockLabel(name)}
      aria-orientation={outside ? 'vertical' : 'horizontal'}
      onMouseEnter={onHold}
      onMouseLeave={onLetGo}
      className={cn(
        'absolute z-20 flex items-center gap-[1px] rounded-md border border-border-subtle bg-surface p-2 shadow-sm',
        outside ? 'flex-col' : '-translate-x-full',
      )}
      style={
        outside
          ? {
              // A tall block scrolled part way out keeps its rail in view.
              top: Math.max(box.top, Math.min(visibleTop + 6, box.top + box.height - 120)),
              left: box.left + box.width + RAIL_GAP,
            }
          : { top: Math.max(0, box.top + 6), left: box.left + box.width - 6 }
      }
      {...testIdProp('email-hand-block-controls')}
    >
      {can.move && (
        <>
          <ControlButton
            label={S.hand.moveUp}
            icon={ArrowUp}
            disabled={busy || !canMove(body, target.path, -1)}
            onClick={() => onMove(-1)}
            testId="email-hand-move-up"
          />
          <ControlButton
            label={S.hand.moveDown}
            icon={ArrowDown}
            disabled={busy || !canMove(body, target.path, 1)}
            onClick={() => onMove(1)}
            testId="email-hand-move-down"
          />
        </>
      )}
      {can.swapImage && (
        <ControlButton
          label={S.hand.swapImage}
          icon={ImageIcon}
          disabled={busy}
          onClick={onSwap}
          testId="email-hand-swap-image"
        />
      )}
      {can.remove && (
        <>
          <span
            aria-hidden
            className={cn('bg-border-subtle', outside ? 'my-4 h-px w-18' : 'mx-4 h-18 w-px')}
          />
          <ControlButton
            label={S.hand.remove}
            icon={Trash2}
            danger
            disabled={busy}
            onClick={onDelete}
            testId="email-hand-delete"
          />
        </>
      )}
    </div>
  );
}

function ControlButton({
  label,
  icon,
  onClick,
  disabled = false,
  pressed,
  danger = false,
  testId,
}: {
  label: string;
  icon: typeof ArrowUp;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  danger?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      // The frame keeps its caret and its selection: the bar never takes the focus on a press.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex size-28 items-center justify-center rounded-[6px] text-text-secondary outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-brand-500 disabled:pointer-events-none disabled:opacity-40',
        danger
          ? 'hover:bg-danger-50 hover:text-danger-700'
          : 'hover:bg-neutral-100 hover:text-text-strong',
      )}
      {...testIdProp(testId)}
    >
      <Icon icon={icon} size={15} aria-hidden />
    </button>
  );
}

// ── The words' bar ─────────────────────────────────────────────────────────────────────────────────

function EditBar({
  kind,
  box,
  visible,
  saving,
  panel,
  slot,
  linkHere,
  linkWords,
  existingLink,
  onBold,
  onItalic,
  onOpenPanel,
  onClosePanel,
  onLink,
  onRemoveLink,
  onFirstName,
  onCancel,
  onDone,
}: {
  kind: string | null;
  box: Rect;
  /** What of the bars' own space the preview shows now, or null when that can't be read. */
  visible: { top: number; bottom: number } | null;
  saving: boolean;
  panel: 'link' | 'merge' | null;
  /** The page's tools row the bar sits in, or null: then it sits under the words. */
  slot: HTMLElement | null;
  /** Where the link the caret is in goes, or null outside a link. */
  linkHere: string | null;
  linkWords: () => string;
  existingLink: () => LinkTarget | null;
  onBold: () => void;
  onItalic: () => void;
  onOpenPanel: (which: 'link' | 'merge') => void;
  onClosePanel: () => void;
  onLink: (target: LinkTarget, words: string) => void;
  onRemoveLink: () => void;
  onFirstName: (fallback: string) => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  const words = kind !== 'button';
  // The bar sits in the page's tools row, outside the email, so it never covers any of it (the KCC hand
  // edit, 2026-11-01: above the words it covered the logo, below them the next block's first line).
  const docked = slot !== null;
  const left = Math.max(0, box.left - 4);
  const below = box.top + box.height + 8;
  // A panel never covers the words it's for: under them, or over them when that's where the room is.
  const panelBelow = visible === null || below + PANEL_ROOM <= visible.bottom;
  const panelAbove = !panelBelow && box.top - PANEL_ROOM >= (visible?.top ?? 0);
  // Without the row, the bar sits under the words and the panel under the bar.
  const panelTop = docked ? below : below + 44;
  const toolbar = (
    <div
      role="toolbar"
      aria-label={S.hand.barLabel}
      className={cn(
        'flex w-fit items-center gap-[1px]',
        docked
          ? 'flex-wrap'
          : 'absolute z-30 rounded-lg border border-border-subtle bg-surface p-3 shadow-md',
      )}
      style={docked ? undefined : { top: below, left }}
      {...testIdProp('email-hand-bar')}
    >
      {words && (
        <>
          {kind !== 'heading' && (
            <ControlButton
              label={S.hand.bold}
              icon={Bold}
              onClick={onBold}
              testId="email-hand-bold"
            />
          )}
          <ControlButton
            label={S.hand.italic}
            icon={Italic}
            onClick={onItalic}
            testId="email-hand-italic"
          />
          <ControlButton
            label={S.hand.link}
            icon={Link2}
            pressed={panel === 'link'}
            onClick={() => (panel === 'link' ? onClosePanel() : onOpenPanel('link'))}
            testId="email-hand-link"
          />
          {linkHere !== null && panel !== 'link' && (
            <span
              title={linkHere}
              className="max-w-[220px] truncate px-6 font-sans text-s12 tracking-givmo text-brand-700"
              {...testIdProp('email-hand-link-address')}
            >
              {linkHere}
            </span>
          )}
          <button
            type="button"
            aria-label={S.hand.firstName}
            title={S.hand.firstName}
            aria-pressed={panel === 'merge'}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => (panel === 'merge' ? onClosePanel() : onOpenPanel('merge'))}
            className="flex h-28 items-center whitespace-nowrap rounded-[6px] px-8 font-sans text-s13 font-w600 tracking-givmo text-text-secondary outline-none hover:bg-neutral-100 hover:text-text-strong focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-brand-500"
            {...testIdProp('email-hand-first-name')}
          >
            {S.firstNameChip}
          </button>
          <span aria-hidden className="mx-3 h-18 w-px bg-border-subtle" />
        </>
      )}
      <Button
        appearance="saas"
        tone="ghost"
        size="sm"
        className="h-28 px-10"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onCancel}
        testId="email-hand-cancel"
      >
        {S.hand.cancel}
      </Button>
      <Button
        appearance="saas"
        tone="primary"
        size="sm"
        className="ml-2 h-28 px-10"
        leadingIcon={<Icon icon={Check} size={13} aria-hidden />}
        loading={saving}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onDone}
        testId="email-hand-done"
      >
        {S.hand.done}
      </Button>
    </div>
  );
  return (
    <>
      {docked ? createPortal(toolbar, slot) : toolbar}
      {panel !== null && (
        <div
          className="absolute z-30"
          style={panelAbove ? { top: box.top - 8, left } : { top: panelTop, left }}
        >
          <div className={panelAbove ? 'absolute bottom-0 left-0' : undefined}>
            {panel === 'link' ? (
              <LinkPanel
                existing={existingLink()}
                words={linkWords()}
                onApply={onLink}
                onRemove={onRemoveLink}
                onClose={onClosePanel}
              />
            ) : (
              <FirstNamePanel onInsert={onFirstName} onClose={onClosePanel} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function PanelFrame({
  label,
  onClose,
  grew = false,
  children,
  testId,
}: {
  label: string;
  onClose: () => void;
  /** Something was added to the panel (an error): it comes fully into view again. */
  grew?: boolean;
  children: ReactNode;
  testId: string;
}) {
  // The preview scrolls and clips: a panel opened low in it comes fully into view, its buttons with it.
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    frame.current?.scrollIntoView?.({ block: 'nearest' });
  }, [grew]);
  return (
    <div
      ref={frame}
      role="dialog"
      aria-label={label}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }}
      className="mt-6 flex w-[320px] flex-col gap-10 rounded-lg border border-border-subtle bg-surface p-14 shadow-md"
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

const inputClass =
  'h-36 w-full rounded-md border border-border-strong bg-surface px-10 font-sans text-s14 tracking-givmo text-text outline-none placeholder:text-text-tertiary focus:border-brand-600 focus:shadow-ring';

function LinkPanel({
  existing,
  words,
  onApply,
  onRemove,
  onClose,
}: {
  existing: LinkTarget | null;
  words: string;
  onApply: (target: LinkTarget, words: string) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(linkInputText(existing ?? undefined));
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const apply = () => {
    const target = parseLinkInput(value);
    if (target === null) {
      setError(S.hand.linkInvalid);
      return;
    }
    onApply(target, words.trim().length > 0 ? words : linkInputText(target));
  };
  return (
    <PanelFrame
      label={S.hand.linkTitle}
      onClose={onClose}
      grew={error !== null}
      testId="email-hand-link-panel"
    >
      <label className="flex flex-col gap-6">
        <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          {S.hand.linkTitle}
        </span>
        <input
          ref={input}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              apply();
            }
          }}
          placeholder={S.hand.linkPlaceholder}
          aria-invalid={error !== null}
          aria-describedby={error !== null ? 'email-hand-link-error' : undefined}
          className={inputClass}
          {...testIdProp('email-hand-link-input')}
        />
      </label>
      {error !== null && (
        <p
          id="email-hand-link-error"
          className="font-sans text-s12 text-danger-700"
          {...testIdProp('email-hand-link-error')}
        >
          {error}
        </p>
      )}
      <button
        type="button"
        className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
        onClick={() =>
          onApply({ kind: 'appeal' }, words.trim().length > 0 ? words : S.hand.appealWords)
        }
        {...testIdProp('email-hand-link-appeal')}
      >
        {existing?.kind === 'appeal' ? S.hand.linkIsAppeal : S.hand.linkToAppeal}
      </button>
      <div className="flex items-center justify-end gap-8">
        {existing !== null && (
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            className="mr-auto"
            onClick={onRemove}
            testId="email-hand-link-remove"
          >
            {S.hand.linkRemove}
          </Button>
        )}
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onClose}
          testId="email-hand-link-cancel"
        >
          {S.hand.cancel}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          size="sm"
          onClick={apply}
          testId="email-hand-link-apply"
        >
          {S.hand.linkApply}
        </Button>
      </div>
    </PanelFrame>
  );
}

function FirstNamePanel({
  onInsert,
  onClose,
}: {
  onInsert: (fallback: string) => void;
  onClose: () => void;
}) {
  const [fallback, setFallback] = useState<string>(S.hand.firstNameDefault);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.select(), []);
  const insert = () => {
    const words = fallback.replace(/\s+/g, ' ').trim();
    onInsert(words.length > 0 ? words.slice(0, 40) : S.hand.firstNameDefault);
  };
  return (
    <PanelFrame label={S.hand.firstName} onClose={onClose} testId="email-hand-first-name-panel">
      <label className="flex flex-col gap-6">
        <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          {S.hand.firstNameFallback}
        </span>
        <input
          ref={input}
          value={fallback}
          maxLength={40}
          onChange={(event) => setFallback(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              insert();
            }
          }}
          className={inputClass}
          {...testIdProp('email-hand-first-name-fallback')}
        />
      </label>
      <div className="flex items-center justify-end gap-8">
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onClose}
          testId="email-hand-first-name-cancel"
        >
          {S.hand.cancel}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          size="sm"
          onClick={insert}
          testId="email-hand-first-name-insert"
        >
          {S.hand.firstNameInsert}
        </Button>
      </div>
    </PanelFrame>
  );
}

// ── The subject and the preview text ───────────────────────────────────────────────────────────────

/**
 * One line of the inbox (the subject or the preview text), edited where it reads: a click turns it into a
 * field; Enter or leaving it saves, Esc puts it back.
 */
export function InboxLineField({
  value,
  label,
  placeholder,
  maxLength,
  enabled,
  strong,
  caption,
  onSave,
  testId,
}: {
  value: string;
  label: string;
  placeholder: string;
  maxLength: number;
  enabled: boolean;
  /** The subject reads bold, as an inbox draws it. */
  strong: boolean;
  /** Words naming the line, before it (the preview text, which nothing else names). */
  caption?: string;
  onSave: (value: string) => Promise<boolean>;
  testId: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (draft !== null) input.current?.focus();
    // Only on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft !== null]);
  const finish = async () => {
    if (draft === null || saving) return;
    const next = draft.replace(/\s+/g, ' ').trim();
    if (next === value.trim()) {
      setDraft(null);
      return;
    }
    setSaving(true);
    const saved = await onSave(next);
    setSaving(false);
    if (saved) setDraft(null);
  };
  const text = cn(
    'font-sans text-s14 tracking-givmo',
    strong ? 'font-w600 text-text-strong' : 'text-text-secondary',
  );
  if (draft !== null) {
    return (
      <input
        ref={input}
        value={draft}
        maxLength={maxLength}
        disabled={saving}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void finish()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void finish();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(null);
          }
        }}
        className={cn(
          text,
          '-mx-6 block h-24 w-[calc(100%+12px)] rounded-[5px] border border-brand-600 bg-surface px-5 outline-none shadow-ring',
        )}
        {...testIdProp(`${testId}-input`)}
      />
    );
  }
  if (!enabled) {
    return (
      <p className={cn(text, 'truncate')} {...testIdProp(testId)}>
        {value}
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        // The assistant's sheet over a narrow page would cover the line being edited and its Done.
        yieldAssistantSheet();
        setDraft(value);
      }}
      aria-label={S.hand.editLine(label)}
      className={cn(
        text,
        'group -mx-6 flex h-24 w-[calc(100%+12px)] items-center gap-6 rounded-[5px] border border-transparent px-5 text-left outline-none hover:border-border-strong hover:bg-neutral-50 focus-visible:border-brand-600',
        value.trim() === '' && 'font-w400 text-text-tertiary',
      )}
      {...testIdProp(testId)}
    >
      {caption !== undefined && (
        <span className="shrink-0 font-w400 text-text-tertiary">{caption}:</span>
      )}
      <span className="min-w-0 truncate">{value.trim() === '' ? placeholder : value}</span>
      <Icon
        icon={Pencil}
        size={12}
        aria-hidden
        className="ml-auto shrink-0 text-text-tertiary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
      />
    </button>
  );
}
